import { useEffect, useMemo, useState } from 'react';
import Avatar from './Avatar.jsx';
import LinkedText from './LinkedText.jsx';
import { MODE_LABEL, placeLabel, whenLabel } from '../lib/format.js';
import { sharedInterests } from '../lib/interests.js';
import { mediaUrl } from '../lib/upload.js';
import AudioNote from './AudioNote.jsx';

/** Body of the map popup. Mapbox anchors it to the marker for us. */
/**
 * Most actionable first: something happening soon beats something recently
 * confirmed, which beats the newest thing. The card shows exactly one.
 */
function topPost(posts) {
  const current = (posts ?? []).filter((p) => p.status === 'live' && p.currentUntil > Date.now());
  if (!current.length) return null;
  const onNow = current.find((post) => post.liveSince);
  if (onNow) return onNow;
  const soon = current
    .filter((p) => p.mode === 'happening' && p.startsAt > Date.now())
    .sort((a, b) => a.startsAt - b.startsAt);
  if (soon.length) return soon[0];
  return current.slice().sort((a, b) =>
    (b.confirmedAt ?? b.createdAt) - (a.confirmedAt ?? a.createdAt))[0];
}

export default function ProfileCard({
  actor, me, posts, isSelf, relation, incoming, onOpenFeed, onMessageAbout,
  onMessage, onAddFriend, onWave, onRespond, onEditProfile, onSignIn, onClose,
}) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const common = useMemo(
    () => sharedInterests(me?.interests, actor.interests),
    [me?.interests, actor.interests]
  );
  // Someone still browsing anonymously has no interests to intersect with, so
  // you reach out about one of your own and they see it when they sign up.
  const guestTarget = !isSelf && !actor.userId;
  const simulated = actor.kind === 'simulation' || String(actor.id).startsWith('s:');
  const options = guestTarget ? (me?.interests ?? []) : common;
  const optionsKey = options.join('\u0000');
  const [topic, setTopic] = useState(options[0] || '');
  const livePosts = (posts ?? []).filter(
    (p) => p.status === 'live' && p.currentUntil > Date.now()
  );
  const top = topPost(posts);

  // The cheapest possible opening. It sends the same connection request the
  // form below does, so nothing downstream has to know a wave happened.
  const waveButton = me && !isSelf && relation === 'none' ? (
    <button
      type="button"
      className="btn btn-sm btn-wave"
      onClick={() => onWave?.(actor)}
      title={`Say hi to ${actor.displayName}`}
      aria-label={`Say hi to ${actor.displayName}`}
    >
      <span className="wave-hand" aria-hidden="true">👋</span> Say hi
    </button>
  ) : null;

  useEffect(() => {
    setTopic((current) => (options.includes(current) ? current : options[0] || ''));
    setAsking(false);
    setNote('');
  }, [actor.id, optionsKey]);


  return (
    <div className="card">
      <button type="button" className="icon-btn card-close" onClick={onClose} aria-label="Close">
        ×
      </button>

      {/* Who they are, and the one tap that needs no thought. Saying hi rides
          up here because it is not a decision — the decisions are below the
          rule. */}
      <div className="card-top">
        <div className="card-identity">
          <Avatar person={actor} size="lg" online={!actor.away} />
          <div className="card-id">
            <div className="card-name">
              {actor.displayName}
              {relation === 'friend' && <span className="star" title="Connection">★</span>}
            </div>
            {/* Where they are, and nothing else. "on now" is the dot on their
                avatar and "2 in common" is the chips below lighting up, so
                putting either into words says it twice and cost this line the
                room to say the one thing only it can. */}
            <div className="card-location">{placeLabel(actor)}</div>
            {actor.doing && (
              <div className="card-doing">
                {[actor.doing.tag, actor.doing.placeName].filter(Boolean).join(' at ')}
              </div>
            )}
          </div>
        </div>
        {waveButton}
      </div>

      {/* Their own words about themselves, before the tags that summarise
          them. Clamped: this is a preview and the profile carries the whole
          thing, so a 500-character bio must not push the actions off the
          bottom of a card floating over a map. */}
      {actor.bio && (
        <p className="card-bio"><LinkedText text={actor.bio} /></p>
      )}

      {/* Common ground first: the shared ones are why this person is worth
          opening, and the rest say who they are. */}
      {(actor.interests?.length ?? 0) > 0 && (
        <div className="card-interests">
          {(common.length > 0 && !isSelf
            ? [...common, ...actor.interests.filter((i) => !common.includes(i))]
            : actor.interests
          ).slice(0, 6).map((interest) => (
            <span
              key={interest}
              className={`interest-chip ${common.includes(interest) && !isSelf ? 'shared' : ''}`}
            >
              {interest}
            </span>
          ))}
        </div>
      )}

      {top && (
        <div className="card-post">
          {/* What is being asked, before what is being said. The rail says the
              same word inline after a name because a row is one line; the card
              has room to give it the gold capsule the design system draws. */}
          {top.mode && (
            <span className="card-mode">
              <span className="card-mode-dot" aria-hidden="true" />
              {MODE_LABEL[top.mode]}
              {top.mode === 'happening' && top.startsAt ? ` · ${whenLabel(top.startsAt)}` : ''}
            </span>
          )}
          {top.body && <p className="card-post-body"><LinkedText text={top.body} /></p>}
          {top.media[0] && (
            top.media[0].kind === 'image' ? (
              <img className="card-post-media" src={mediaUrl(top.media[0].id)} alt="" />
            ) : (
              <AudioNote
                src={mediaUrl(top.media[0].id)}
                peaks={top.media[0].peaks}
                durationMs={top.media[0].durationMs}
                compact
                download={false}
              />
            )
          )}
        </div>
      )}

      <div className="card-actions">
        {!me ? (
          <>
            <p className="card-note">
              {isSelf
                ? 'This is you. Sign in to claim it.'
                : 'You’re browsing as a guest. Sign in to interact.'}
            </p>
            <button type="button" className="btn btn-primary btn-block" onClick={onSignIn}>
              Sign in
            </button>
          </>
        ) : isSelf ? (
          <button type="button" className="btn btn-block" onClick={onEditProfile}>
            Edit your profile
          </button>
        ) : (relation === 'friend' || (actor.userId && relation !== 'incoming' && !asking)) ? (
          <>
            {/* Saying hello no longer requires asking permission first. What is
                rationed is the opening line, not the ability to write one. */}
            {/* One Message. When they are carrying something, messaging is
                about that — which is what the second button used to do from
                inside the post, directly above this one and in the same
                words. */}
            <div className="primary-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => (top ? onMessageAbout?.(top) : onMessage(actor))}
              >
                {top ? 'Message about this' : 'Message'}
              </button>
              {actor.username && (
                <button type="button" className="btn" onClick={onOpenFeed}>View profile</button>
              )}
            </div>
            {/* Connecting asks for a topic and a note, so it is a considered
                thing and sits under the pair rather than among them. */}
            {relation === 'none' && !simulated && options.length > 0 && (
              <button type="button" className="btn btn-block" onClick={() => setAsking(true)}>
                {guestTarget ? `Reach out about ${options[0]}` : `Connect about ${options[0]}`}
              </button>
            )}
            {relation === 'sent' && <p className="card-note">Connection request sent.</p>}
          </>
        ) : relation === 'incoming' ? (
          <>
            {incoming?.topic && (
              <p className="connection-context">Common ground · <b>{incoming.topic}</b></p>
            )}
            {incoming?.note && <p className="card-quote">“{incoming.note}”</p>}
            <p className="card-note">They’d like to connect.</p>
            <div className="btn-row">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => onRespond(incoming.id, true)}
              >
                Accept
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => onRespond(incoming.id, false)}
              >
                Decline
              </button>
            </div>
          </>
        ) : relation === 'sent' ? (
          <button type="button" className="btn btn-block" disabled>
            {guestTarget ? 'Sent — waiting for them to sign up' : 'Connection request sent'}
          </button>
        ) : asking ? (
          <form
            onSubmit={(ev) => {
              ev.preventDefault();
              onAddFriend(actor, topic, note.trim());
              setAsking(false);
              setNote('');
            }}
          >
            <div className="field">
              <label>
                {guestTarget ? 'Reach out about' : 'Common ground'}
                <span className="label-hint"> · optional</span>
              </label>
              {options.length > 0 ? (
                <div className="interest-list">
                  {options.map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      className={`interest-chip selectable ${topic === interest ? 'selected' : ''}`}
                      onClick={() => setTopic((current) => (current === interest ? '' : interest))}
                      aria-pressed={topic === interest}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="hint">
                  {guestTarget
                    ? 'No interests on your profile yet — say hello anyway.'
                    : 'Nothing in common listed yet — reach out anyway.'}
                </p>
              )}
            </div>
            <div className="field">
              <label htmlFor="fr-note">Add a note (optional)</label>
              <textarea
                id="fr-note"
                value={note}
                maxLength={200}
                rows={2}
                autoFocus
                placeholder="What would you like to exchange about this?"
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="btn-row">
              <button type="submit" className="btn btn-primary btn-sm">Send connection</button>
              <button type="button" className="btn btn-sm" onClick={() => setAsking(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            {guestTarget && (
              <p className="card-note">
                Still browsing anonymously — they’ll see this and can accept once they sign up.
              </p>
            )}
            <div className="btn-row">
              {waveButton}
              <button type="button" className="btn btn-primary btn-grow" onClick={() => setAsking(true)}>
                {options.length > 0
                  ? (guestTarget ? `Reach out about ${options[0]}` : `Connect about ${options[0]}`)
                  : (guestTarget ? 'Reach out' : 'Connect')}
              </button>
            </div>
          </>
        )}

        {/* Everyone can read the whole person. Those who can also message get
            it as half of the pair above; this is the case for everyone else. */}
        {actor.username && !(me && !isSelf && (relation === 'friend' || (actor.userId && relation !== 'incoming' && !asking))) && (
          <button type="button" className="btn btn-block" onClick={onOpenFeed}>
            {livePosts.length > 1 ? `View profile · ${livePosts.length - 1} more` : 'View profile'}
          </button>
        )}
      </div>
    </div>
  );
}
